/**
 * Lean OpenAPI 3 loader for APIPod / FastAPI services.
 * The JS counterpart of fastSDK's spec loading, reduced to what Socaity
 * services emit: fetch `openapi.json`, resolve `$ref`s, and map operations
 * into a `ServiceDefinition` the runtime can submit against.
 */

import {
  EndpointDefinition,
  EndpointParameter,
  ParameterDefinition,
  ServiceDefinition,
  serviceAddressFromUrl,
} from '../../schemas/definitions';

const BODY_CONTENT_PREFERENCE = [
  'application/json',
  'multipart/form-data',
  'application/x-www-form-urlencoded',
];

/** Maps APIPod `x-media-type` schema markers to parameter formats. */
const MEDIA_TYPE_FORMATS: Record<string, string> = {
  ImageFile: 'image',
  AudioFile: 'audio',
  VideoFile: 'video',
  MediaFile: 'file',
  Asset3DFile: 'file',
};

/** Load a service's OpenAPI spec and parse it into a ServiceDefinition. */
export async function loadServiceDefinition(
  url: string,
  options: { fetchOptions?: RequestInit } = {},
): Promise<ServiceDefinition> {
  const specUrl = url.endsWith('openapi.json')
    ? url
    : `${url.replace(/\/+$/, '')}/openapi.json`;
  const response = await fetch(specUrl, options.fetchOptions);
  if (!response.ok) {
    throw new Error(`Could not load OpenAPI spec from ${specUrl} (HTTP ${response.status})`);
  }
  const spec = await response.json();
  const serviceUrl = url.endsWith('openapi.json') ? url.slice(0, -'/openapi.json'.length) : url;
  return parseOpenApiSpec(spec, serviceUrl);
}

/** Parse an already-loaded OpenAPI 3 document. */
export function parseOpenApiSpec(spec: Record<string, any>, serviceUrl: string): ServiceDefinition {
  const schemas = spec.components?.schemas ?? {};
  const endpoints: EndpointDefinition[] = [];

  for (const [path, operations] of Object.entries<Record<string, any>>(spec.paths ?? {})) {
    for (const [method, operation] of Object.entries<any>(operations)) {
      const upperMethod = method.toUpperCase();
      if (upperMethod !== 'GET' && upperMethod !== 'POST') continue;
      endpoints.push(parseOperation(path, upperMethod as 'GET' | 'POST', operation, schemas));
    }
  }

  return {
    id: spec.info?.title,
    display_name: spec.info?.title,
    description: spec.info?.description,
    version: spec.info?.version,
    specification: 'apipod',
    endpoints,
    service_address: serviceAddressFromUrl(serviceUrl),
  };
}

// ---------------------------------------------------------------------------
// Internals
// ---------------------------------------------------------------------------

function resolveRef(schema: any, schemas: Record<string, any>): any {
  if (schema?.$ref) {
    const name = schema.$ref.split('/').pop();
    return schemas[name] ?? {};
  }
  return schema ?? {};
}

function parseOperation(
  path: string,
  method: 'GET' | 'POST',
  operation: Record<string, any>,
  schemas: Record<string, any>,
): EndpointDefinition {
  const parameters: EndpointParameter[] = [];

  for (const param of operation.parameters ?? []) {
    parameters.push({
      name: param.name,
      location: param.in ?? 'query',
      required: param.required ?? false,
      default: param.schema?.default,
      description: param.description,
      definition: toDefinitions(param.schema ?? {}, schemas),
      param_schema: param.schema,
    });
  }

  let contentType: string | undefined;
  let supportsStreaming = false;

  const content = operation.requestBody?.content ?? {};
  contentType = BODY_CONTENT_PREFERENCE.find((candidate) => candidate in content)
    ?? Object.keys(content)[0];

  if (contentType) {
    const bodySchema = resolveRef(content[contentType].schema, schemas);
    const required: string[] = bodySchema.required ?? [];
    supportsStreaming = 'stream' in (bodySchema.properties ?? {});

    for (const [name, rawProperty] of Object.entries<any>(bodySchema.properties ?? {})) {
      parameters.push({
        name,
        location: 'body',
        required: required.includes(name),
        default: rawProperty.default,
        description: rawProperty.description,
        definition: toDefinitions(rawProperty, schemas),
        param_schema: rawProperty,
      });
    }
  }

  return {
    id: operation.operationId,
    display_name: operation.summary,
    description: operation.description,
    path,
    method,
    parameters,
    request_body_content_type: contentType,
    supports_streaming: supportsStreaming,
    timeout_hint_s: operation['x-timeout-s'],
  };
}

/** Flatten a property schema (incl. anyOf/oneOf/$ref/array) into definitions. */
function toDefinitions(
  property: Record<string, any>,
  schemas: Record<string, any>,
): ParameterDefinition | ParameterDefinition[] {
  const variants = property.anyOf ?? property.oneOf ?? property.allOf;
  if (Array.isArray(variants)) {
    const definitions = variants
      .map((variant) => toDefinition(variant, schemas))
      .filter((definition) => definition.type !== 'null');
    return definitions.length === 1 ? definitions[0] : definitions;
  }
  return toDefinition(property, schemas);
}

function toDefinition(schema: Record<string, any>, schemas: Record<string, any>): ParameterDefinition {
  const resolved = resolveRef(schema, schemas);

  const mediaType = resolved['x-media-type'];
  if (mediaType || looksLikeFileModel(resolved)) {
    return { type: 'object', format: MEDIA_TYPE_FORMATS[mediaType] ?? 'file' };
  }

  if (resolved.type === 'array') {
    const item = toDefinition(resolved.items ?? {}, schemas);
    return { type: 'array', format: item.format };
  }

  if (resolved.type === 'string' && resolved.format === 'binary') {
    return { type: 'string', format: 'binary' };
  }

  return {
    type: resolved.type,
    format: resolved.format,
    enum: resolved.enum,
    minimum: resolved.minimum,
    maximum: resolved.maximum,
    minLength: resolved.minLength,
    maxLength: resolved.maxLength,
  };
}

function looksLikeFileModel(schema: Record<string, any>): boolean {
  const properties = schema.properties ?? {};
  return 'file_name' in properties && 'content_type' in properties && 'content' in properties;
}
